import React from "react";
import { Link, Navigate } from "react-router-dom";

import Badge from "ui/Badge";
import Grid from "ui/Grid";
import Loading from "ui/Loading";
import Panel from "ui/Panel";
import PanelSection from "ui/PanelSection";
import PageContent from "ui/PageContent";
import SettingsNav from "ui/SettingsNav";
import { useQueryStringSearch } from "ui/QueryStringSearch";
import { useCurrentOrganization } from "providers/WithCurrentOrganization";
import { makeFilter } from "utils/filter";
import { useRoutes } from "utils/routes";

type RowDataType = {
  id: string;
  name: string;
  type: "Cluster" | "Server Group";
  clusterId: string | null;
};

const ServerGroupSettingsList = () => {
  const currentOrganization = useCurrentOrganization();
  const [searchTerm, SearchInput] = useQueryStringSearch("q");

  if (currentOrganization == null) {
    return <Loading error />;
  }

  if (!currentOrganization.enabledFeatures.serverGroupCrud) {
    return (
      <Navigate to={`/organizations/${currentOrganization.slug}`} replace />
    );
  }

  const gridData: RowDataType[] = [
    ...currentOrganization.serverGroups.map(
      (sg): RowDataType => ({
        id: sg.id,
        name: sg.name,
        type: "Cluster",
        clusterId: sg.clusterId,
      }),
    ),
    ...currentOrganization.customServerGroups.map(
      (sg): RowDataType => ({
        id: sg.id,
        name: sg.name,
        type: "Server Group",
        clusterId: null,
      }),
    ),
  ].filter(makeFilter(searchTerm, "name"));

  return (
    <PageContent
      title="Settings"
      pageCategory="servergroups"
      pageName="settings"
      featureNav={<SettingsNav />}
    >
      <Panel title="All Server Groups" secondaryTitle={<SearchInput />}>
        <PanelSection>Select a server group to view settings:</PanelSection>
        <Grid
          striped
          defaultSortBy="name"
          data={gridData}
          columns={[
            {
              field: "name",
              header: "Server Group",
              width: "minmax(30%,1fr)",
              renderer: ServerGroupNameCell,
            },
            {
              field: "type",
              header: "Type",
              width: "20%",
              className: "flex items-center",
              renderer: ServerGroupTypeCell,
            },
            {
              field: "clusterId",
              header: "Cluster ID",
              width: "minmax(20%,25%)",
              nullValue: "—",
            },
          ]}
        />
      </Panel>
    </PageContent>
  );
};

const ServerGroupNameCell = ({ rowData }: { rowData: RowDataType }) => {
  const { serverGroupEdit } = useRoutes();
  return (
    <span>
      <Link to={serverGroupEdit(rowData.id)}>{rowData.name}</Link>
    </span>
  );
};

const ServerGroupTypeCell = ({ rowData }: { rowData: RowDataType }) => {
  return rowData.type === "Cluster" ? (
    <Badge className="border font-medium !rounded-md !py-[2px] !px-[3px] !ml-0 !text-xs">
      Cluster
    </Badge>
  ) : (
    <Badge className="border font-medium !rounded-md !py-[2px] !px-[3px] !ml-0 !text-xs !bg-[#ede9fe] !text-[#6d28d9] !border-[#c4b5fd]">
      Server Group
    </Badge>
  );
};

export default ServerGroupSettingsList;
